import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useChildCheckinData from "layouts/tables/data/childCheckinData";

function ChildCheckIn() {
  const [tab, setTab] = useState(0);

  const {
    childColumns,
    children,
    isLoading,
    error,
    sessionColumns,
    sessions,
    isLoadingSessions,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    codeModalOpen,
    setCodeModalOpen,
    checkoutModalOpen,
    setCheckoutModalOpen,
    selectedChild,
    newCheckin,
    checkoutCode,
    setCheckoutCode,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    guardianSearch,
    setGuardianSearch,
    guardianResults,
    handleSelectGuardian,
    handleCheckoutConfirm,
  } = useChildCheckinData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading children...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Child Check-In"
        subtitle="Sunday school safety pairing — check children in, issue a pickup code, and verify guardians at checkout."
        actions={
          tab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              New Child Profile
            </Button>
          )
        }
      />

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Children" />
          <Tab label={`Today's Check-Ins (${sessions.length})`} />
        </Tabs>

        {tab === 0 ? (
          <DataTable
            table={{ columns: childColumns, rows: children }}
            isSorted
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            showTotalEntries
            canSearch
            searchLabel="Search children..."
          />
        ) : isLoadingSessions ? (
          <Typography sx={{ p: 3 }}>Loading sessions...</Typography>
        ) : (
          <DataTable
            table={{ columns: sessionColumns, rows: sessions }}
            isSorted
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            showTotalEntries
            canSearch
            searchLabel="Search sessions..."
          />
        )}
      </Card>

      {/* Create / Edit child */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={form.id ? "Edit Child Profile" : "New Child Profile"}
        onSave={handleSubmit}
        saveLabel={isSaving ? "Saving..." : form.id ? "Save Changes" : "Create Profile"}
        saveDisabled={!form.first_name.trim() || !form.surname.trim() || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="First Name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Surname"
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
          </Box>

          <Box sx={{ position: "relative" }}>
            <TextField
              label="Guardian"
              placeholder="Search member..."
              value={guardianSearch}
              onChange={(e) => {
                setGuardianSearch(e.target.value);
                setForm((prev) => ({ ...prev, guardian_member_id: null }));
              }}
              fullWidth
              margin="normal"
            />
            {guardianResults.length > 0 && (
              <Card
                sx={{
                  position: "absolute",
                  zIndex: 10,
                  width: "100%",
                  mt: -1,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {guardianResults.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => handleSelectGuardian(m)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {m.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">
                      {m.first_name} {m.surname}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>

          <TextField
            label="Date of Birth"
            type="date"
            value={form.date_of_birth}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Allergies"
            value={form.allergies}
            onChange={(e) => setForm({ ...form, allergies: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </FormSection>
      </FormDialog>

      {/* Pickup code display */}
      <FormDialog
        open={codeModalOpen}
        onClose={() => setCodeModalOpen(false)}
        title="Checked In"
        onSave={() => setCodeModalOpen(false)}
        saveLabel="Done"
        maxWidth="xs"
      >
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="body1" mb={1}>
            {newCheckin?.child?.first_name} {newCheckin?.child?.surname} is checked in.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Give this code to the guardian — it&apos;s required at pickup.
          </Typography>
          <Typography variant="h2" fontWeight={800} letterSpacing={6} color="success.main">
            {newCheckin?.security_code}
          </Typography>
        </Box>
      </FormDialog>

      {/* Checkout verification */}
      <FormDialog
        open={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        title="Verify Pickup Code"
        onSave={handleCheckoutConfirm}
        saveLabel="Check Out"
        saveDisabled={!checkoutCode.trim()}
        maxWidth="xs"
      >
        <TextField
          label="Security Code"
          value={checkoutCode}
          onChange={(e) => setCheckoutCode(e.target.value)}
          fullWidth
          margin="normal"
          autoFocus
        />
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete the profile for "${selectedChild?.name}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default ChildCheckIn;
